import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import CoachDialog from "@/components/CoachDialog";
import { getPin } from "@/lib/api";

// Plain-language description of each page, fed to the assistant as context.
const PAGE_CONTEXT = {
  "/": "the Start screen — picking which till to close (Pro Shop or Makwa Grill) and the close mode",
  "/dashboard": "the Owner dashboard listing past closes with over/short badges and review flags",
  "/tips": "the Tip Log — the pooled tip ledger and cash payouts from the tip float box",
  "/bridge": "the Bridge sheet — side-by-side Pro Shop vs Makwa Grill department revenue",
  "/help": "the Help page",
};

const POS_KEY = "agc_coach_fab_pos";

// Clamp the saved position inside the current viewport so it can never end up off-screen
// after a window resize / device rotation.
function clampPos(p, size) {
  if (!p) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.max(8, Math.min(vw - size.w - 8, p.x)),
    y: Math.max(8, Math.min(vh - size.h - 8, p.y)),
  };
}

/** A single floating assistant available on every authenticated page.
 *  Hidden on the login screen and on the close wizard / close-detail pages,
 *  which render their own context-rich assistant.
 *  The pill is draggable — drag-and-release to reposition; saved to localStorage. */
export default function GlobalCoach() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // {x,y} or null = default bottom-right
  const [dragging, setDragging] = useState(false);
  const movedRef = useRef(false);
  const startRef = useRef(null);
  const btnRef = useRef(null);
  const { pathname } = useLocation();

  // Load saved position on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved.x === "number" && typeof saved.y === "number") {
          setPos(saved);
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Keep it on-screen if the window is resized.
  useEffect(() => {
    const onResize = () => {
      if (!pos || !btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      const next = clampPos(pos, { w: r.width, h: r.height });
      if (next && (next.x !== pos.x || next.y !== pos.y)) setPos(next);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos]);

  const onPointerDown = (e) => {
    if (!btnRef.current) return;
    btnRef.current.setPointerCapture?.(e.pointerId);
    const r = btnRef.current.getBoundingClientRect();
    startRef.current = {
      grabX: e.clientX - r.left,
      grabY: e.clientY - r.top,
      w: r.width,
      h: r.height,
      x0: e.clientX,
      y0: e.clientY,
    };
    movedRef.current = false;
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!dragging || !startRef.current) return;
    const { grabX, grabY, w, h, x0, y0 } = startRef.current;
    // Treat tiny moves as a click, not a drag.
    if (!movedRef.current && Math.hypot(e.clientX - x0, e.clientY - y0) < 6) return;
    movedRef.current = true;
    const next = clampPos({ x: e.clientX - grabX, y: e.clientY - grabY }, { w, h });
    if (next) setPos(next);
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (movedRef.current && pos) {
      try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
    }
  };

  const onClick = (e) => {
    if (movedRef.current) {
      // It was a drag, not a click — swallow.
      e.preventDefault();
      e.stopPropagation();
      movedRef.current = false;
      return;
    }
    setOpen(true);
  };

  if (!getPin()) return null;
  if (pathname === "/login" || pathname.startsWith("/close/") || pathname.startsWith("/close-detail/")) return null;

  const where = PAGE_CONTEXT[pathname] || "the AGC Daily Close app";
  const ctx = `The user is on ${where}. This is the AGC Daily Close app for Aguasabon Golf Club (Pro Shop float $300, Makwa Grill float $150, Masters Bar float $300 — the Masters Bar shares the Grill's Moneris terminal, so its card receipts are kept and reconciled manually; card tips are paid in cash from the locked tip float box, never out of the till; card_sales = Moneris total − tips). Answer their question in plain, blame-free language.`;

  // Position style: drag-positioned (pos != null) wins over default bottom-right.
  const style = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto", touchAction: "none" }
    : { right: 20, bottom: 96, touchAction: "none" };

  return (
    <>
      <button
        ref={btnRef}
        data-testid="global-coach-fab"
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={style}
        className={`fixed z-40 h-10 px-3.5 rounded-full bg-[#0D311E] text-[#FDFBF7] shadow-lg shadow-[#0D311E]/25 flex items-center justify-center gap-1.5 select-none ${dragging ? "cursor-grabbing scale-105" : "cursor-grab active:scale-95"} transition-transform`}
      >
        <MessageSquare size={14} />
        <span className="text-xs font-medium">Ask</span>
      </button>
      <CoachDialog contextText={ctx} open={open} onOpenChange={setOpen} />
    </>
  );
}
